/**
 * ThemePanels — Site theme + Logo ring + CTA layout admin panels.
 *   - SiteThemePanel: page background image + gradient + spotlight.
 *   - LogoRingPanel:  ring around the site logo (style, thickness, colors, animation).
 *   - CtaLayoutPanel: sizing of the 6 CTA tiles below the hero.
 */
import React, { useRef, useState } from "react";
import { Upload, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { setPath } from "./setPath.js";
import { SelectField, RangeField, ColorField } from "./fields.jsx";

export function SiteThemePanel({ cfg, setCfg }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const t = cfg.site_theme || {};
  const setT = (k, v) => setCfg(setPath(cfg, ["site_theme", k], v));

  const bgImg = t.bg_image_url || "";
  const fullBgUrl = bgImg.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${bgImg}` : bgImg;

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Apenas ficheiros de imagem."); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Imagem demasiado grande (máx 8 MB)."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/homepage-config/upload-image?kind=site_bg", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data?.url;
      if (!url) throw new Error("sem URL");
      setT("bg_image_url", url);
      toast.success(`Fundo carregado (${(res.data.bytes/1024).toFixed(0)} KB)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload falhou");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="site-theme-panel">
      <div>
        <Label className="text-sm font-bold text-slate-700">Imagem de fundo (página inteira)</Label>
        <p className="text-[11px] text-slate-500 mb-2">Substitui o gradiente. Tamanho máx 8 MB. Formatos: PNG, JPG, WEBP, GIF.</p>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="bg-image-file"
        />
        <div className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} className="rounded-lg flex-shrink-0" data-testid="bg-image-upload-btn">
            <Upload className="w-4 h-4 mr-1.5" /> {busy ? "A carregar…" : "Carregar imagem"}
          </Button>
          <Input
            value={bgImg}
            onChange={(e) => setT("bg_image_url", e.target.value)}
            placeholder="/api/static/site_bg_*.png ou URL completo"
            data-testid="bg-image-url"
          />
          {bgImg && (
            <Button variant="outline" onClick={() => setT("bg_image_url", "")} className="rounded-lg flex-shrink-0 text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="bg-image-clear">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
        {fullBgUrl && (
          <div className="mt-3 rounded-lg overflow-hidden border border-slate-200 bg-slate-900" data-testid="bg-image-preview">
            <img src={fullBgUrl} alt="bg preview" className="w-full max-h-64 object-cover" />
          </div>
        )}
      </div>

      {bgImg && (
        <>
        <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
          <div>
            <Label className="text-xs text-slate-600">Opacidade da imagem ({t.bg_image_opacity ?? 100}%)</Label>
            <input type="range" min={0} max={100} value={t.bg_image_opacity ?? 100} onChange={(e) => setT("bg_image_opacity", Number(e.target.value))} className="w-full mt-1" data-testid="bg-image-opacity" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Encaixe</Label>
            <select value={t.bg_image_size || "cover"} onChange={(e) => setT("bg_image_size", e.target.value)} className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="bg-image-size">
              <option value="cover">Cover (cobre toda a área)</option>
              <option value="contain">Contain (caber sem cortes)</option>
              <option value="100% 100%">Esticar 100%</option>
              <option value="auto">Auto (tamanho original)</option>
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Posição (preset)</Label>
            <select value={t.bg_image_position || "center top"} onChange={(e) => setT("bg_image_position", e.target.value)} className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="bg-image-position">
              <option value="center top">Centro topo</option>
              <option value="center center">Centrado</option>
              <option value="center bottom">Centro fundo</option>
              <option value="left top">Esquerda topo</option>
              <option value="right top">Direita topo</option>
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Repetição</Label>
            <select value={t.bg_image_repeat || "no-repeat"} onChange={(e) => setT("bg_image_repeat", e.target.value)} className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="bg-image-repeat">
              <option value="no-repeat">Sem repetir</option>
              <option value="repeat">Repetir (tile)</option>
              <option value="repeat-x">Repetir horizontal</option>
              <option value="repeat-y">Repetir vertical</option>
            </select>
          </div>
        </div>

        <div className="rounded-lg border border-sky-100 bg-sky-50/60 p-3 space-y-2" data-testid="bg-fine-position">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-black text-sky-800 uppercase tracking-wide">Posição fina (X/Y)</p>
            <button
              type="button"
              onClick={() => { setT("bg_image_pos_x", null); setT("bg_image_pos_y", null); }}
              className="text-[10px] text-sky-700 underline hover:text-sky-900"
              data-testid="bg-fine-reset"
            >
              Usar preset acima
            </button>
          </div>
          <div>
            <Label className="text-xs">Posição horizontal ({t.bg_image_pos_x ?? 50}%)</Label>
            <Input type="range" min={0} max={100} step={1} value={t.bg_image_pos_x ?? 50} onChange={(e) => setT("bg_image_pos_x", Number(e.target.value))} data-testid="bg-pos-x" />
          </div>
          <div>
            <Label className="text-xs">Posição vertical ({t.bg_image_pos_y ?? 0}%)</Label>
            <Input type="range" min={-100} max={200} step={1} value={t.bg_image_pos_y ?? 0} onChange={(e) => setT("bg_image_pos_y", Number(e.target.value))} data-testid="bg-pos-y" />
            <p className="text-[10px] text-sky-800/80 mt-1">
              0% = topo · 50% = centro · 100% = fundo · valores fora do intervalo empurram para além do ecrã (útil se a imagem for maior que a viewport).
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 space-y-2 mt-2">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-black text-emerald-800 uppercase tracking-wide">Arrastar imagem (X/Y em px) + Zoom</p>
            <button
              type="button"
              className="text-[10px] text-emerald-700 hover:text-emerald-900 underline"
              onClick={() => { setT("bg_image_translate_x", 0); setT("bg_image_translate_y", 0); setT("bg_image_zoom", 100); }}
              data-testid="bg-pan-reset"
            >Repor</button>
          </div>
          <p className="text-[10px] text-emerald-800/80">
            Move a imagem para cima/baixo/esquerda/direita <b>sem perder a cobertura</b> (a imagem continua sempre a cobrir toda a página). Aumenta o zoom para teres margem extra para deslocar.
          </p>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">← Esquerda / Direita → ({t.bg_image_translate_x ?? 0}px)</Label>
              <Input type="range" min={-800} max={800} step={5} value={t.bg_image_translate_x ?? 0} onChange={(e) => setT("bg_image_translate_x", Number(e.target.value))} data-testid="bg-translate-x" />
            </div>
            <div>
              <Label className="text-xs">↑ Cima / Baixo ↓ ({t.bg_image_translate_y ?? 0}px)</Label>
              <Input type="range" min={-800} max={800} step={5} value={t.bg_image_translate_y ?? 0} onChange={(e) => setT("bg_image_translate_y", Number(e.target.value))} data-testid="bg-translate-y" />
            </div>
            <div>
              <Label className="text-xs">Zoom ({t.bg_image_zoom ?? 100}%)</Label>
              <Input type="range" min={100} max={300} step={5} value={t.bg_image_zoom ?? 100} onChange={(e) => setT("bg_image_zoom", Number(e.target.value))} data-testid="bg-zoom" />
              <p className="text-[10px] text-emerald-800/70 mt-1">100% = cobre exacto · &gt;100% dá espaço para arrastar sem mostrar áreas em branco.</p>
            </div>
          </div>
        </div>
        </>
      )}

      <div className="grid sm:grid-cols-2 gap-4 pt-3 border-t border-slate-100">
        <ColorField label="Gradiente — cor topo" value={t.bg_start || "#0A0E2A"} onChange={(v) => setT("bg_start", v)} testid="theme-bg-start" />
        <ColorField label="Gradiente — cor fundo" value={t.bg_end || "#1B1352"} onChange={(v) => setT("bg_end", v)} testid="theme-bg-end" />
        <ColorField label="Cor da Spotlight (topo)" value={t.spotlight_color || "#4ECDC4"} onChange={(v) => setT("spotlight_color", v)} testid="theme-spot-color" />
        <div>
          <Label className="text-xs text-slate-600">Intensidade da Spotlight ({t.spotlight_intensity ?? 30}%)</Label>
          <input type="range" min={0} max={100} value={t.spotlight_intensity ?? 30} onChange={(e) => setT("spotlight_intensity", Number(e.target.value))} className="w-full mt-1" data-testid="theme-spot-intensity" />
        </div>
      </div>
    </div>
  );
}

export function LogoRingPanel({ cfg, setCfg }) {
  const r = cfg.logo_ring || {};
  const setRing = (key, value) => setCfg(setPath(cfg, ["logo_ring", key], value));
  const enabled = r.enabled !== false;

  const previewStyle = (() => {
    const size = r.logo_size || 56;
    const thick = r.thickness ?? 3;
    const dist  = r.distance ?? 2;
    const outer = size + 2 * (thick + dist);
    let bg = r.color_primary || "#000";
    if (r.style === "rainbow") {
      const c1 = r.color_primary   || "#000000";
      const c2 = r.color_glow      || "#FF00C8";
      const c3 = r.color_secondary || "#FFD93D";
      bg = `conic-gradient(from 0deg,${c1},${c2},${c3},${c2},${c1})`;
    }
    if (r.style === "gradient") bg = `linear-gradient(135deg,${r.color_primary||"#000"},${r.color_secondary||"#FFD93D"})`;
    const s = { width: outer, height: outer, padding: thick, background: bg, borderRadius: "9999px" };
    const dir = r.direction || "normal";
    if ((r.intensity || 0) > 0 || r.animation === "glow") {
      const i = r.intensity || 25;
      s.boxShadow = `0 0 ${i/2}px ${i/4}px ${r.color_glow||"#FF00C8"}`;
    }
    if (r.animation === "rotate") s.animation = `brand-spin ${r.speed||6}s linear infinite ${dir}`;
    if (r.animation === "pulse")  s.animation = `brand-pulse ${r.speed||6}s ease-in-out infinite`;
    if (r.style === "dashed") { s.background = "transparent"; s.border = `${thick}px dashed ${r.color_primary||"#000"}`; s.padding = 0; }
    return { outer: s, inner: { padding: dist, background: "white", borderRadius: "9999px", width: "100%", height: "100%" }, size };
  })();

  return (
    <div className="mt-5 border-t border-slate-100 pt-4" data-testid="logo-ring-panel">
      <div className="flex items-center justify-between mb-3">
        <Label className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
          <Sparkles className="w-4 h-4 text-sky-500" /> Anel em redor do logo
        </Label>
        <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
          <input type="checkbox" checked={enabled} onChange={(e) => setRing("enabled", e.target.checked)} className="rounded" data-testid="logo-ring-enabled" />
          Ativar
        </label>
      </div>

      <fieldset disabled={!enabled} className={enabled ? "" : "opacity-50 pointer-events-none"}>
        <div className="grid sm:grid-cols-4 gap-3 mb-3">
          <SelectField label="Estilo" value={r.style || "rainbow"} onChange={(v) => setRing("style", v)} testid="logo-ring-style"
            options={[{v:"rainbow",l:"Arco-íris"},{v:"solid",l:"Cor sólida"},{v:"gradient",l:"Gradiente (2 cores)"},{v:"dashed",l:"Tracejado"}]} />
          <RangeField label="Espessura" value={r.thickness ?? 3} min={1} max={12} unit="px" onChange={(v) => setRing("thickness", v)} testid="logo-ring-thickness" />
          <RangeField label="Distância" value={r.distance ?? 2} min={0} max={10} unit="px" onChange={(v) => setRing("distance", v)} testid="logo-ring-distance" />
          <SelectField label="Animação" value={r.animation || "glow"} onChange={(v) => setRing("animation", v)} testid="logo-ring-animation"
            options={[{v:"none",l:"Nenhuma"},{v:"glow",l:"Brilho (glow)"},{v:"rotate",l:"Rotação"},{v:"pulse",l:"Pulso"}]} />
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mb-3">
          <RangeField label="Velocidade" value={r.speed ?? 6} min={1} max={20} unit="s" onChange={(v) => setRing("speed", v)} testid="logo-ring-speed" />
          <SelectField label="Direção" value={r.direction || "normal"} onChange={(v) => setRing("direction", v)} testid="logo-ring-direction"
            options={[{v:"normal",l:"Normal (↺)"},{v:"reverse",l:"Reverso (↻)"},{v:"alternate",l:"Alternada (↔)"}]} />
          <RangeField label="Intensidade" value={r.intensity ?? 25} min={0} max={100} unit="%" onChange={(v) => setRing("intensity", v)} testid="logo-ring-intensity" />
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mb-3">
          <ColorField label="Cor principal" value={r.color_primary || "#000000"} onChange={(v) => setRing("color_primary", v)} testid="logo-ring-color-primary" />
          <ColorField label="Cor secundária (gradiente)" value={r.color_secondary || "#FFD93D"} onChange={(v) => setRing("color_secondary", v)} testid="logo-ring-color-secondary" />
          <ColorField label="Cor do brilho (glow)" value={r.color_glow || "#FF00C8"} onChange={(v) => setRing("color_glow", v)} testid="logo-ring-color-glow" />
        </div>

        <RangeField label="Tamanho do logo" value={r.logo_size ?? 56} min={32} max={160} unit="px" onChange={(v) => setRing("logo_size", v)} testid="logo-ring-size" />
      </fieldset>

      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold mb-2">Pré-visualização</div>
        <div className="bg-slate-50 rounded-lg p-6 flex items-center justify-center" data-testid="logo-ring-preview">
          {enabled ? (
            <div style={previewStyle.outer}>
              <div style={previewStyle.inner}>
                {cfg.logo_url ? (
                  <img src={cfg.logo_url} alt="preview" className="w-full h-full object-cover rounded-full bg-white" />
                ) : (
                  <div className="w-full h-full rounded-full bg-blue-600 grid place-items-center text-white text-lg font-bold">S★</div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ width: previewStyle.size, height: previewStyle.size }} className="rounded-full overflow-hidden bg-white">
              {cfg.logo_url ? <img src={cfg.logo_url} alt="preview" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-blue-600 grid place-items-center text-white">S★</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function CtaLayoutPanel({ cfg, setCfg }) {
  const L = cfg.cta_layout || {};
  const set = (k, v) => setCfg(setPath(cfg, ["cta_layout", k], v));
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 mb-3" data-testid="cta-layout-panel">
      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">Tamanho dos botões CTA</div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-600">Largura total ({L.max_width ?? 672}px)</Label>
          <input type="range" min={320} max={1600} step={4} value={L.max_width ?? 672} onChange={(e) => set("max_width", Number(e.target.value))} className="w-full mt-1" data-testid="cta-max-width" />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Espaço entre botões ({L.gap ?? 12}px)</Label>
          <input type="range" min={0} max={32} step={1} value={L.gap ?? 12} onChange={(e) => set("gap", Number(e.target.value))} className="w-full mt-1" data-testid="cta-gap" />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Espaço vertical entre linhas ({L.row_gap ?? 10}px)</Label>
          <input type="range" min={0} max={120} step={1} value={L.row_gap ?? 10} onChange={(e) => set("row_gap", Number(e.target.value))} className="w-full mt-1" data-testid="cta-row-gap" />
          <p className="text-[10px] text-slate-400">distância vertical entre a linha de botões primários e a linha dos secundários</p>
        </div>
        <div>
          <Label className="text-xs text-slate-600">Cantos arredondados ({L.radius ?? 14}px)</Label>
          <input type="range" min={0} max={40} step={1} value={L.radius ?? 14} onChange={(e) => set("radius", Number(e.target.value))} className="w-full mt-1" data-testid="cta-radius" />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Ícone (altura {L.icon_pct ?? 54}%)</Label>
          <input type="range" min={20} max={90} step={1} value={L.icon_pct ?? 54} onChange={(e) => set("icon_pct", Number(e.target.value))} className="w-full mt-1" data-testid="cta-icon-pct" />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Altura linha SUPERIOR ({L.primary_height ?? 52}px)</Label>
          <input type="range" min={40} max={400} step={2} value={L.primary_height ?? 52} onChange={(e) => set("primary_height", Number(e.target.value))} className="w-full mt-1" data-testid="cta-primary-height" />
          <p className="text-[10px] text-slate-400">altura em px (largura é dividida automaticamente)</p>
        </div>
        <div>
          <Label className="text-xs text-slate-600">Altura linha INFERIOR ({L.secondary_height ?? 52}px)</Label>
          <input type="range" min={40} max={400} step={2} value={L.secondary_height ?? 52} onChange={(e) => set("secondary_height", Number(e.target.value))} className="w-full mt-1" data-testid="cta-secondary-height" />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Texto botões PRIMÁRIOS (linha superior · {L.text_primary ?? 15}px)</Label>
          <input type="range" min={9} max={30} step={1} value={L.text_primary ?? 15} onChange={(e) => set("text_primary", Number(e.target.value))} className="w-full mt-1" data-testid="cta-text-primary" />
          <p className="text-[10px] text-slate-400">tamanho fixo do texto nos 3 botões grandes de cima</p>
        </div>
        <div>
          <Label className="text-xs text-slate-600">Texto botões SECUNDÁRIOS (linha inferior · {L.text_secondary ?? 13}px)</Label>
          <input type="range" min={9} max={30} step={1} value={L.text_secondary ?? 13} onChange={(e) => set("text_secondary", Number(e.target.value))} className="w-full mt-1" data-testid="cta-text-secondary" />
          <p className="text-[10px] text-slate-400">tamanho fixo do texto nos 3 botões pequenos de baixo</p>
        </div>
        <div>
          <Label className="text-xs text-slate-600">Rotação ícone PRIMÁRIO ({L.icon_rotate_primary ?? 0}°)</Label>
          <input type="range" min={-180} max={180} step={5} value={L.icon_rotate_primary ?? 0} onChange={(e) => set("icon_rotate_primary", Number(e.target.value))} className="w-full mt-1" data-testid="cta-icon-rot-primary" />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Rotação texto PRIMÁRIO ({L.text_rotate_primary ?? 0}°)</Label>
          <input type="range" min={-45} max={45} step={1} value={L.text_rotate_primary ?? 0} onChange={(e) => set("text_rotate_primary", Number(e.target.value))} className="w-full mt-1" data-testid="cta-text-rot-primary" />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Rotação ícone SECUNDÁRIO ({L.icon_rotate_secondary ?? 0}°)</Label>
          <input type="range" min={-180} max={180} step={5} value={L.icon_rotate_secondary ?? 0} onChange={(e) => set("icon_rotate_secondary", Number(e.target.value))} className="w-full mt-1" data-testid="cta-icon-rot-secondary" />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Rotação texto SECUNDÁRIO ({L.text_rotate_secondary ?? 0}°)</Label>
          <input type="range" min={-45} max={45} step={1} value={L.text_rotate_secondary ?? 0} onChange={(e) => set("text_rotate_secondary", Number(e.target.value))} className="w-full mt-1" data-testid="cta-text-rot-secondary" />
        </div>
      </div>
    </div>
  );
}
